import { create } from 'zustand';
import { zoneService, type ZoneProfile } from '../services/zoneService';

interface ZoneProfileStore {
  zones: ZoneProfile[];
  loading: boolean;
  error: string | null;

  fetchZones: (networkId?: string | null) => Promise<void>;
  updateZone: (zoneId: string, updates: Partial<ZoneProfile>) => Promise<void>;
  upsertZone: (zoneId: string, discoveredName: string, networkId?: string | null) => Promise<void>;
  deleteZone: (zoneId: string) => Promise<void>;
  markSeen: (zoneId: string, networkId?: string | null) => Promise<void>;
  reorderZones: (zoneIds: string[]) => Promise<void>;
  updateNetworkLabel: (networkId: string, label: string) => Promise<void>;
}

export const useZoneProfileStore = create<ZoneProfileStore>((set, get) => ({
  zones: [],
  loading: false,
  error: null,

  fetchZones: async (networkId) => {
    set({ loading: true, error: null });
    try {
      const { zones } = await zoneService.getProfiles(networkId);
      set({ zones, loading: false });
    } catch (e: any) {
      set({ error: e?.message || 'Failed to load zones', loading: false });
    }
  },

  updateZone: async (zoneId, updates) => {
    try {
      const { zone } = await zoneService.updateProfile(zoneId, updates);
      set({
        zones: get().zones.map((z) => (z.zone_id === zoneId ? zone : z)),
      });
    } catch (e: any) {
      console.warn('[ZoneProfileStore] update failed:', e?.message);
    }
  },

  upsertZone: async (zoneId, discoveredName, networkId) => {
    try {
      const { zone } = await zoneService.upsertProfile(zoneId, discoveredName, networkId);
      const existing = get().zones;
      const match = (z: ZoneProfile) =>
        z.zone_id === zoneId && (networkId ? z.network_id === networkId : !z.network_id);
      const idx = existing.findIndex(match);
      if (idx >= 0) {
        set({ zones: existing.map((z, i) => (i === idx ? zone : z)) });
      } else {
        set({ zones: [zone, ...existing] });
      }
    } catch (e: any) {
      console.warn('[ZoneProfileStore] upsert failed:', e?.message);
    }
  },

  deleteZone: async (zoneId) => {
    try {
      await zoneService.deleteProfile(zoneId);
      set({ zones: get().zones.filter((z) => z.zone_id !== zoneId) });
    } catch (e: any) {
      console.warn('[ZoneProfileStore] delete failed:', e?.message);
    }
  },

  markSeen: async (zoneId, networkId) => {
    try {
      await zoneService.markSeen(zoneId, networkId);
    } catch (e: any) {
      console.warn('[ZoneProfileStore] markSeen failed:', e?.message);
    }
  },

  reorderZones: async (zoneIds) => {
    const reordered = zoneIds
      .map((id, i) => {
        const z = get().zones.find((z) => z.zone_id === id);
        return z ? { ...z, sort_order: i } : null;
      })
      .filter(Boolean) as ZoneProfile[];
    set({ zones: reordered });
    try {
      await zoneService.reorderProfiles(zoneIds);
    } catch (e: any) {
      console.warn('[ZoneProfileStore] reorder failed:', e?.message);
      get().fetchZones();
    }
  },

  updateNetworkLabel: async (networkId, label) => {
    try {
      await zoneService.updateNetworkLabel(networkId, label);
      set({
        zones: get().zones.map((z) =>
          z.network_id === networkId ? { ...z, network_label: label } : z,
        ),
      });
      // Re-fetch to pick up the backend state in case no zones matched optimistically
      const { zones: fresh } = await zoneService.getProfiles();
      set({ zones: fresh });
    } catch (e: any) {
      console.warn('[ZoneProfileStore] updateNetworkLabel failed:', e?.message);
    }
  },
}));
